import { lazy } from "react";
import { Navigate } from "react-router-dom";

// Helper for named exports
function lazyImport<
  T extends React.ComponentType<any>,
  I extends { [K2 in K]: T },
  K extends keyof I,
>(factory: () => Promise<I>, name: K): React.LazyExoticComponent<T> {
  return lazy(() => factory().then((module) => ({ default: module[name] })));
}

const Dashboard = lazyImport(
  () => import("../pages/Dashboard/Dashboard"),
  "Dashboard",
);
const Campaigns = lazyImport(
  () => import("../pages/Campaigns/Campaigns"),
  "Campaigns",
);
const TrackingLinks = lazyImport(
  () => import("../pages/TrackingLinks/TrackingLinks"),
  "TrackingLinks",
);
const ComponentsDemo = lazyImport(
  () => import("../pages/ComponentsDemo/ComponentsDemo"),
  "ComponentsDemo",
);
const Team = lazyImport(() => import("../pages/Team/Team"), "Team");
const Domains = lazyImport(() => import("../pages/Domains/Domains"), "Domains");
const Integrations = lazyImport(
  () => import("../pages/Integrations/Integrations"),
  "Integrations",
);
const Billing = lazyImport(() => import("../pages/Billing/Billing"), "Billing");
const Login = lazyImport(() => import("../pages/Login/Login"), "Login");
const Landings = lazyImport(
  () => import("../pages/Landings/Landings"),
  "Landings",
);
const LandingDetail = lazyImport(
  () => import("../pages/Landings/LandingDetail"),
  "LandingDetail",
);
const Spots = lazyImport(() => import("../pages/Spots/Spots"), "Spots");
const SpotDetail = lazyImport(
  () => import("../pages/Spots/SpotDetail"),
  "SpotDetail",
);
const TermsOfService = lazyImport(
  () => import("../pages/Legal/TermsOfService"),
  "TermsOfService",
);
const Docs = lazyImport(() => import("../pages/Docs/Docs"), "Docs");
const PrivacyPolicy = lazyImport(
  () => import("../pages/Legal/PrivacyPolicy"),
  "PrivacyPolicy",
);
const Pricing = lazyImport(() => import("../pages/Pricing/Pricing"), "Pricing");
const Register = lazyImport(
  () => import("../pages/Register/Register"),
  "Register",
);
const Notifications = lazyImport(
  () => import("../pages/Notifications/Notifications"),
  "Notifications",
);
const VerifyEmail = lazyImport(
  () => import("../pages/VerifyEmail/VerifyEmail"),
  "VerifyEmail",
);
const Home = lazyImport(() => import("../pages/Home/Home"), "Home");
const ForgotPassword = lazyImport(
  () => import("../pages/ForgotPassword/ForgotPassword"),
  "ForgotPassword",
);
const ResetPassword = lazyImport(
  () => import("../pages/ForgotPassword/ResetPassword"),
  "ResetPassword",
);
const CampaignDetails = lazyImport(
  () => import("../pages/Campaigns/CampaignDetails"),
  "CampaignDetails",
);
const Account = lazyImport(() => import("../pages/Account/Account"), "Account");
const Clicks = lazyImport(() => import("../pages/Analytics/Clicks"), "Clicks");
const Conversions = lazyImport(
  () => import("../pages/Analytics/Conversions"),
  "Conversions",
);
const Leads = lazyImport(() => import("../pages/Leads/Leads"), "default");
const AcceptInvite = lazyImport(
  () => import("../pages/Team/AcceptInvite"),
  "AcceptInvite",
);
const AdminTeams = lazyImport(
  () => import("../pages/Admin/AdminTeams"),
  "AdminTeams",
);
const AdminUsers = lazyImport(
  () => import("../pages/Admin/AdminUsers"),
  "AdminUsers",
);
const AdminStats = lazyImport(
  () => import("../pages/Admin/AdminStats"),
  "AdminStats",
);
const AdminSupport = lazyImport(
  () => import("../pages/Admin/AdminSupport"),
  "AdminSupport",
);
const TicketDetail = lazyImport(
  () => import("../pages/Support/TicketDetail"),
  "TicketDetail",
);
const SupportTicketPage = lazyImport(
  () => import("../pages/Support/SupportTicketPage"),
  "SupportTicketPage",
);
const GetStarted = lazyImport(
  () => import("../pages/GetStarted/GetStarted"),
  "default",
);
const Lakehouse = lazyImport(
  () => import("../pages/Lakehouse/Lakehouse"),
  "Lakehouse",
);


export type SidebarSection =
  "main" | "campaigns" | "assets" | "analytics" | "settings" | "none";

export interface AppRoute {
  path: string;
  label: string;
  i18nKey?: string;
  element: JSX.Element;
  section: SidebarSection;
  isPublic?: boolean;
  /** Public route that logged-in users should be redirected away from. */
  guestOnly?: boolean;
  openInNewTab?: boolean;
}

export const appRoutes: AppRoute[] = [
  // ПУБЛІЧНІ
  {
    path: "",
    label: "2DATracker",
    element: <Home />,
    section: "none",
    isPublic: true,
  },
  {
    path: "login",
    label: "Login",
    i18nKey: "nav:login",
    element: <Login />,
    section: "none",
    isPublic: true,
    guestOnly: true,
  },
  {
    path: "register",
    label: "Register",
    element: <Register />,
    section: "none",
    isPublic: true,
    guestOnly: true,
  },
  {
    path: "verify",
    label: "Verify Email",
    element: <VerifyEmail />,
    section: "none",
    isPublic: true,
  },
  {
    path: "forgot-password",
    label: "Forgot Password",
    element: <ForgotPassword />,
    section: "none",
    isPublic: true,
  },
  {
    path: "reset-password",
    label: "Reset Password",
    element: <ResetPassword />,
    section: "none",
    isPublic: true,
  },
  {
    path: "terms",
    label: "Terms",
    i18nKey: "nav:terms",
    element: <TermsOfService />,
    section: "none",
    isPublic: true,
  },
  {
    path: "privacy",
    label: "Privacy",
    i18nKey: "nav:privacy",
    element: <PrivacyPolicy />,
    section: "none",
    isPublic: true,
  },
  {
    path: "pricing",
    label: "Pricing",
    i18nKey: "nav:pricing",
    element: <Pricing />,
    section: "none",
    isPublic: true,
  },
  {
    path: "docs",
    label: "Docs",
    i18nKey: "nav:docs",
    element: <Docs />,
    section: "settings",
  },
  {
    path: "accept-invite",
    label: "Accept Invite",
    element: <AcceptInvite />,
    section: "none",
    isPublic: true,
  },
  {
    path: "lakehouse",
    label: "Lakehouse and BI",
    element: <Lakehouse />,
    section: "none",
    isPublic: true,
  },


  // ЗАХИЩЕНІ (DASHBOARD)
  {
    path: "get-started",
    label: "Get Started",
    i18nKey: "nav:get_started",
    element: <GetStarted />,
    section: "settings",
  },
  {
    path: "dashboard",
    label: "Dashboard",
    i18nKey: "nav:dashboard",
    element: <Dashboard />,
    section: "main",
  },
  {
    path: "campaigns",
    label: "Campaigns",
    i18nKey: "nav:campaigns",
    element: <Campaigns />,
    section: "campaigns",
  },
  {
    path: "campaigns/:id",
    label: "Campaign Details",
    element: <CampaignDetails />,
    section: "none",
  },
  {
    path: "links",
    label: "Tracking Links",
    i18nKey: "nav:tracking_links",
    element: <TrackingLinks />,
    section: "assets",
  },
  {
    path: "clicks",
    label: "Clicks Log",
    i18nKey: "nav:clicks",
    element: <Clicks />,
    section: "analytics",
  },
  {
    path: "conversions",
    label: "Conversions",
    i18nKey: "nav:conversions",
    element: <Conversions />,
    section: "analytics",
  },
  {
    path: "leads",
    label: "Subscribers",
    i18nKey: "nav:leads",
    element: <Leads />,
    section: "analytics",
  },
  {
    path: "landings",
    label: "Landings",
    i18nKey: "nav:landings",
    element: <Landings />,
    section: "assets",
  },
  {
    path: "landings/:id",
    label: "Landing Detail",
    element: <LandingDetail />,
    section: "none",
  },
  {
    path: "spots",
    label: "Spots",
    i18nKey: "nav:spots",
    element: <Spots />,
    section: "assets",
  },
  {
    path: "spots/:id",
    label: "Spot Detail",
    element: <SpotDetail />,
    section: "none",
  },
  {
    path: "team",
    label: "Team Workspace",
    i18nKey: "nav:team",
    element: <Team />,
    section: "settings",
  },
  {
    path: "account",
    label: "Account",
    i18nKey: "nav:account",
    element: <Account />,
    section: "none", // Не показуємо в боковому меню, бо є доступ через хедер
  },
  {
    path: "domains",
    label: "Custom Domains",
    i18nKey: "nav:domains",
    element: <Domains />,
    section: "settings",
  },
  {
    path: "integrations",
    label: "Integrations",
    i18nKey: "nav:integrations",
    element: <Integrations />,
    section: "settings",
  },
  {
    path: "notifications",
    label: "Notifications",
    i18nKey: "nav:notifications",
    element: <Notifications />,
    section: "none",
  },
  {
    path: "support/:id",
    label: "Complaint Details",
    element: <TicketDetail />,
    section: "none",
  },
  {
    path: "admin/support/ticket/:id", // Backwards compatibility for old notifications
    label: "Complaint Details (Legacy)",
    element: <TicketDetail />,
    section: "none",
  },
  {
    path: "billing",
    label: "Platform Limits",
    i18nKey: "nav:pricing",
    element: <Navigate to="/pricing" replace />,
    section: "none",
  },
  // Платформ-адмінка (SUPER_ADMIN). Не в меню — доступ через дропдаун юзера в хедері.
  {
    path: "admin/teams",
    label: "Admin Teams",
    element: <AdminTeams />,
    section: "none",
  },
  {
    path: "admin/users",
    label: "Admin Users",
    element: <AdminUsers />,
    section: "none",
  },
  {
    path: "admin/stats",
    label: "Admin Stats",
    element: <AdminStats />,
    section: "none",
  },
  {
    path: "admin/support",
    label: "Admin Support",
    element: <AdminSupport />,
    section: "none",
  },
  {
    path: "support/:ticketId",
    element: <SupportTicketPage />,
    isPublic: false,
    section: "none",
    label: "Support Ticket",
  },
];

export const ROUTES = appRoutes.reduce(
  (acc, route) => {
    const key = route.label.toUpperCase().replace(/[^A-Z0-9]/g, "_");
    acc[key] = route.path;
    return acc;
  },
  {} as Record<string, string>,
);
